import {
    peekProviderReturn,
    isLegacyProviderReturnState,
} from "../components/integrations/providerReturnParams";
import { projectSwitchPath, routeContract } from "../utils/routeContract";
import { isLeaveQuestionPending, requestLeave } from "../shell/leaveGuardStore";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useStytchMember, useStytchMemberSession } from "@stytch/react/b2b";
import { useLocation } from "react-router-dom";
import { Alert, Box, Button, Modal, ModalDialog, ModalClose, Stack, Typography } from "@mui/joy";
import axios, { startNativeAuthBootstrap } from "../config/axios";
import { shellHoldsSession } from "../platform/shellSession";
import {
    admissionDeadline,
    admissionTiming,
    requestCompanySession,
    routeSelector,
    type CompanySessionAdmission,
} from "../api/companySession";
import {
    REFUSAL,
    clearCompanySignInMarker,
    clearFallbackMarker,
    clearRefusalReloadMarker,
    fellBackFromProjectUrl,
    reloadForRefusal,
    companySignInWasTried,
    redirectToUnprefixedRoute,
    refusalInfo,
    unprefixedUrl,
} from "../utils/admissionRefusal";
import { isTransientAdmissionFailure } from "../api/companySession";
import {
    forgetLastProject,
    lastProjectSource,
    readLastProject,
    rememberLastProject,
    type LastProject,
} from "../utils/lastProject";
import { signInRequirement } from "../auth/authMethods";
import CompanySignInButton from "../components/common/CompanySignInButton";
import { useEmbedAuth } from "./EmbedAuthContext";
import type {
    SessionTenancy,
    TenantCompany,
    TenantProject,
    TenantSessionResponse,
} from "../shared/interfaces";
import {
    credentialDeadline,
    currentTabCompanySession,
    deferRenewal,
    hasPreloadLife,
    installTabCompanySession,
    setTenantSessionRevalidator,
    usableTabCompanySession,
} from "./tabCompanySession";
import { AdmissionNoticeReporter, admissionFailureCode } from "./admissionNotice";
import {
    closeTenantScopeForDocument,
    captureTenantScopeBoundary,
    installValidatedScope,
    installSelectedCompany,
    isTenantScopeAdmitted,
    isTenantScopeDocumentClosed,
    resetTenantScope,
} from "./tenantScopeHeaders";
import { buildCapabilityFingerprint, buildScopeKey } from "../util/scopeKey";
import { sessionBoundProject, routeNamesBoundProject } from "../utils/sessionBoundProject";
import { openInBrowser } from "../native/openInBrowser";
import { sessionIdentityFromJwt } from "../platform/tenantIdentity";
import {
    tenantPath,
    tenantRoute,
    replaceBootstrapTenantPath,
    oauthTenantContinuation,
    projectPickerContinuation,
} from "../utils/tenantRoute";
import TenantScopeSwitcher from "../components/TenantScopeSwitcher";
import AdmissionShell from "../components/AdmissionShell";
import { getPlatform } from "../platform/platformRef";
import { offerAdmittedAccess } from "../access/admissionClient";
import {
    handOffPrefetchedAdmission,
    prefetchAdmission,
    prefetchLanding,
} from "./admissionPrefetch";
import { takeAdmissionHandoff, type AdmissionRoute } from "../utils/admissionHandoff";
import { projectStorageOrgSlug } from "../shared/tenantProjectSlug";
import { supersedeLegacyFileCache } from "../util/fileCacheScopeIndex";
import AdmissionRefusal, { type RefusalScreen } from "../components/AdmissionRefusal";
import {
    clearAdmittedNativeScope,
    ensureNativeTenantScopeSupport,
    installAdmittedNativeScope,
    NativeTenantScopeUnsupportedError,
} from "../native/nativeAdmittedScope";

import {
    TenantScopeContext,
    TenantScopeValue,
    EMPTY,
    EMPTY_CAPABILITIES,
} from "./TenantScopeContext";
const styles = {
    status: { maxWidth: 520, mx: "auto", mt: "12vh", px: 3 },
};
const TIMEOUT_MS = 20_000;
const RENEWAL_TIMEOUT_MS = 15_000;
class AdmissionRejected extends Error {}

/** Whether the session this app is signed in to is bound to the project the URL names. */
async function namesSessionProject(route: ReturnType<typeof tenantRoute>): Promise<boolean> {
    try {
        const { data } = await axios.get<TenantSessionResponse>("/api/ai/session/me", {
            timeout: TIMEOUT_MS,
        });
        const bound = sessionBoundProject(parseSessionTenancy(data.tenancy));
        return !!bound && routeNamesBoundProject(route, bound);
    } catch {
        return false;
    }
}

type LegacyEntry =
    | {
          hinted: CompanySessionAdmission;
          companySlug: string;
          projectSlug: string;
          deadline: number;
          receivedAt: number;
      }
    | { data: TenantSessionResponse; sentAt: number; receivedAt: number; hintKept: boolean };

/**
 * Bootstrap for a URL with no project prefix. The project this browser session opened last is
 * admitted first and, when admitted, replaces `/session/me`. A refusal forgets it. One that
 * cannot be reached right now is kept for the next entry, and this one opens as `/session/me`
 * says.
 */
async function discoverLegacyEntry(hint: LastProject | null): Promise<LegacyEntry> {
    let hintKept = false;
    if (hint) {
        try {
            const {
                data: hinted,
                deadline,
                receivedAt,
            } = await requestCompanySession(
                // Its answer is the canonical page's first admission, so it carries the access body.
                {
                    company_slug: hint.companySlug,
                    project_slug: hint.projectSlug,
                    include_project_access: true,
                },
                {
                    timeout: TIMEOUT_MS,
                    retries: 0,
                    backoffMs: admissionTiming.initialBackoffMs,
                    signal: new AbortController().signal,
                    // The caller drops a superseded answer; the hint itself has nothing to fence.
                    assertCurrent: () => undefined,
                },
            );
            const companySlug = hinted?.canonical_company_slug;
            const projectSlug = hinted?.canonical_project_slug;
            if (typeof companySlug === "string" && typeof projectSlug === "string")
                return { hinted, companySlug, projectSlug, deadline, receivedAt };
            forgetLastProject();
        } catch (failure) {
            if (isTransientAdmissionFailure(failure)) hintKept = true;
            else forgetLastProject();
        }
    }
    const sentAt = Date.now();
    const { data } = await axios.get<TenantSessionResponse>("/api/ai/session/me", {
        timeout: TIMEOUT_MS,
    });
    return { data, sentAt, receivedAt: Date.now(), hintKept };
}

/** Validate only the additive directory descriptor; null/absent is a valid legacy response. */
export function parseSessionTenancy(value: unknown): SessionTenancy | null {
    if (!value || typeof value !== "object") return null;
    const candidate = value as SessionTenancy;
    if (
        !["legacy_org", "project", "company"].includes(candidate.auth_scope) ||
        typeof candidate.is_platform_identity !== "boolean" ||
        (candidate.url_routing !== undefined && typeof candidate.url_routing !== "boolean") ||
        (candidate.canonical_redirect_project_id !== undefined &&
            candidate.canonical_redirect_project_id !== null &&
            typeof candidate.canonical_redirect_project_id !== "string") ||
        (candidate.master_organization !== undefined &&
            typeof candidate.master_organization !== "boolean") ||
        (candidate.legacy_project_id !== null && typeof candidate.legacy_project_id !== "string") ||
        (candidate.projects_next_cursor !== undefined &&
            candidate.projects_next_cursor !== null &&
            typeof candidate.projects_next_cursor !== "string") ||
        (candidate.companies_next_cursor !== undefined &&
            candidate.companies_next_cursor !== null &&
            typeof candidate.companies_next_cursor !== "string") ||
        !Array.isArray(candidate.companies) ||
        !Array.isArray(candidate.projects)
    )
        return null;
    const companyIds = new Set<string>();
    const companySlugs = new Set<string>();
    for (const company of candidate.companies) {
        if (
            !company ||
            typeof company.company_id !== "string" ||
            !company.company_id ||
            typeof company.name !== "string" ||
            typeof company.state !== "string" ||
            typeof company.slug !== "string" ||
            !/^[a-z0-9][a-z0-9_-]*$/i.test(company.slug) ||
            !Array.isArray(company.capabilities) ||
            company.capabilities.some((c) => typeof c !== "string") ||
            companyIds.has(company.company_id) ||
            companySlugs.has(company.slug)
        )
            return null;
        companyIds.add(company.company_id);
        companySlugs.add(company.slug);
    }
    const projectIds = new Set<string>();
    const projectSlugs = new Set<string>();
    for (const project of candidate.projects) {
        const slugKey = `${project?.company_id}/${project?.slug}`;
        if (
            !project ||
            typeof project.project_id !== "string" ||
            !project.project_id ||
            !companyIds.has(project.company_id) ||
            typeof project.name !== "string" ||
            typeof project.state !== "string" ||
            typeof project.slug !== "string" ||
            !/^[a-z0-9][a-z0-9_-]*$/i.test(project.slug) ||
            !["super_admin", "project_admin", "member"].includes(project.role) ||
            !Array.isArray(project.capabilities) ||
            project.capabilities.some((c) => typeof c !== "string") ||
            projectIds.has(project.project_id) ||
            projectSlugs.has(slugKey)
        )
            return null;
        projectIds.add(project.project_id);
        projectSlugs.add(slugKey);
    }
    return candidate;
}
function validIdentity(session: TenantSessionResponse): boolean {
    return (
        !!session?.member?.member_id &&
        typeof session.member.email_address === "string" &&
        typeof session.member.name === "string" &&
        Array.isArray(session.member.roles) &&
        session.member.roles.every((role) => typeof role?.role_id === "string") &&
        !!session.organization?.organization_id &&
        !!session.organization.organization_slug &&
        typeof session.session?.expires_at === "string"
    );
}

/** Admission lives above the query cache. Its state and asynchronous setters must
 * therefore retire independently when the source account changes. */
function useSourceState<T>(source: object, initial: T) {
    const current = useRef(source);
    current.current = source;
    const [state, setState] = useState({ source, value: initial });
    const set = React.useCallback(
        (next: React.SetStateAction<T>) => {
            if (current.current !== source) return;
            setState((previous) => {
                if (current.current !== source) return previous;
                const value = previous.source === source ? previous.value : initial;
                return {
                    source,
                    value: typeof next === "function" ? (next as (value: T) => T)(value) : next,
                };
            });
        },
        [source, initial],
    );
    return [state.source === source ? state.value : initial, set] as const;
}

/** `/session/me`'s staff registry answer (ai_engine `SessionMeResponse`). Kept here: the shared
 *  interfaces module is at its size ceiling (`code_size_allowlist.py`). */
type StaffRegistryAware = TenantSessionResponse & {
    staff_registry_session?: boolean;
    staff_registry_organization_id?: string | null;
};

/** The admitted project's legacy storage slug, as an optional field of the native scope. */
function storageSlugField(slug: string | null | undefined): { storageOrgSlug?: string } {
    return slug ? { storageOrgSlug: slug } : {};
}

/** The two things admission reads about where the app is: the page's path (which carries the
 *  company/project) and its history key (a new key is a new page). */
export interface TenantScopeLocation {
    pathname: string;
    key: string;
}

/** Admission on this router's location (the web, iOS). */
export function TenantScopeProvider({ children }: { children: React.ReactNode }) {
    const location = useLocation();
    return <TenantScopeProviderAt location={location}>{children}</TenantScopeProviderAt>;
}

/** Whether the person's sign-in is known. Where the app shell holds the session
 *  (`shellHoldsSession`), the Stytch JS client is empty and always says "no session": the sign-in
 *  is known once the shell's restore answers (`startNativeAuthBootstrap`; until it is registered
 *  there is nothing to wait on yet, and the next render looks again). Deciding before then sent a
 *  signed-in desktop page that opened on a project path to `/authenticate`. */
function useShellSessionKnown(): boolean {
    const [known, setKnown] = useState(() => !shellHoldsSession());
    useEffect(() => {
        if (known) return;
        const restore = startNativeAuthBootstrap();
        if (!restore) return;
        let live = true;
        void restore
            .catch(() => null)
            .then(() => {
                if (live) setKnown(true);
            });
        return () => {
            live = false;
        };
    });
    return known;
}

/** The admission itself, on a location the caller supplies: `TenantScopeProvider` passes its
 *  router's; a host whose routers sit below admission (the desktop's per-tab routers) passes the
 *  page's own location, which names the admitted project and nothing else. */
export function TenantScopeProviderAt({
    location,
    children,
    onAdmissionFailed,
}: {
    location: TenantScopeLocation;
    children: React.ReactNode;
    /** Told once admission settles on a failure that will not clear by itself (its message),
     *  for a host whose page has somewhere else to go (a desktop project page). */
    onAdmissionFailed?: (message: string) => void;
}) {
    const source = useStytchMemberSession();
    const sourceMember = useStytchMember();
    const embed = useEmbedAuth();
    const route = tenantRoute();
    const canonical = !!route.companySlug;
    const callback = peekProviderReturn();
    const callbackRestored = useRef<{ state: string; path: string } | null>(null);
    const [restoringCallback, setRestoringCallback] = useState(
        () => !canonical && !!callback?.params.state,
    );
    const shellSessionKnown = useShellSessionKnown();
    const sourceReady = embed.isAuthenticated || (shellSessionKnown && source.isInitialized);
    const sourcePresent = !!source.session || embed.isAuthenticated;
    const embedIdentity = sessionIdentityFromJwt(embed.tokens?.sessionJwt);
    const sourceIdentity = JSON.stringify([
        sourceMember.member?.member_id,
        source.session?.organization_id,
        source.session?.member_session_id,
        embed.isAuthenticated,
        embedIdentity?.organizationId,
        embedIdentity?.memberId,
    ]);
    // The browser's own session owns the last-project hint; an embed panel, or a host that
    // cannot run on project URLs, switches projects by moving its session instead.
    const hintSource =
        !embed.isAuthenticated && getPlatform().projectAdmission.supportsUrlRouting
            ? lastProjectSource(
                  source.session?.organization_id,
                  sourceMember.member?.member_id,
                  source.session?.member_session_id,
              )
            : null;
    const [attempt, setAttempt] = useState(0);
    const sourceAdmission = useMemo(() => ({ sourceIdentity }), [sourceIdentity]);
    const [discoveryState, setDiscoveryState] = useSourceState<"pending" | "ready" | "failed">(
        sourceAdmission,
        "pending",
    );
    const [data, setData] = useSourceState<TenantSessionResponse | null>(sourceAdmission, null);
    const [selection, setSelection] = useSourceState<{
        company: TenantCompany;
        project: TenantProject | null;
        actingAsStaff: boolean;
    } | null>(sourceAdmission, null);
    const [ready, setReady] = useSourceState(sourceAdmission, false);
    const [error, setError] = useSourceState<{
        message: string;
        authenticate: boolean;
        sessionExpired?: boolean;
        unsupported?: boolean;
        /** A refusal retrying cannot change: shown with the project chooser. */
        refusal?: RefusalScreen;
        /** A step-up's sign-in methods, as Stytch named them. */
        signInMethods?: string[] | null;
        /** An outage that clears on its own: the app stays mounted and retries itself. */
        transient?: boolean;
    } | null>(sourceAdmission, null);
    const failure = error && !error.transient ? error.message : null;
    const onFailed = useRef(onAdmissionFailed);
    onFailed.current = onAdmissionFailed;
    useEffect(() => {
        if (failure) onFailed.current?.(failure);
    }, [failure]);
    /** Set by the automatic retry so the warning stays up instead of flickering. */
    const quietRetry = useRef(false);
    // The admission now running, if any: automatic recovery joins it rather than restarting it.
    const admissionInFlight = useRef<AbortController | null>(null);
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const [projectsLoading, setProjectsLoading] = useSourceState(sourceAdmission, false);
    const [routingChoices, setRoutingChoices] = useSourceState<SessionTenancy | null>(
        sourceAdmission,
        null,
    );
    /** An admission discovery already holds, with its credential's deadline, timed from when the
     *  request that brought it was sent, and when it arrived. */
    const bootstrapAdmission = useRef<{
        source: string;
        value: CompanySessionAdmission;
        deadline: number;
        receivedAt: number;
    } | null>(null);
    const legacyDiscovery = useRef<{
        key: string;
        request: Promise<LegacyEntry>;
        /** Asked with the remembered project: a page that is not a project page asks without it. */
        hinted: boolean;
    } | null>(null);
    /** The route a bare entry opened only because the remembered project was unreachable: its
     *  admission is not what the person chose, so it does not replace the hint. */
    const unrememberedRoute = useRef<string | null>(null);
    const [notices] = useState(() => new AdmissionNoticeReporter());
    const runtimeKey = useRef<string | null>(null);
    /** URL prefix the live admission belongs to. A renewal of that exact scope
     *  keeps it; a different company/project URL establishes a new one. */
    const admittedRoute = useRef<string | null>(null);
    const sourcePrincipal = useRef<string | null>(null);
    const basename = route.basename;
    const legacyPathname = canonical ? null : location.pathname;
    const authFlow = /^\/(authenticate|embed-auth)(\/|$)/.test(location.pathname);

    useEffect(() => {
        if (!sourceReady) return;
        let active = true;
        if (!canonical) {
            const pendingCallback = peekProviderReturn();
            const state = pendingCallback?.params.state;
            if (state && callbackRestored.current?.state !== state) {
                if (!sourcePresent) {
                    window.location.replace(
                        `/authenticate?redirect=${encodeURIComponent(window.location.pathname)}`,
                    );
                    return;
                }
                setRestoringCallback(true);
                setError(null);
                const sentAt = Date.now();
                void axios
                    .post<{ selection: CompanySessionAdmission | null; return_path: string }>(
                        "/api/ai/session/oauth-continuation",
                        { state },
                        { timeout: TIMEOUT_MS },
                    )
                    .then(({ data: continuation }) => {
                        const receivedAt = Date.now();
                        if (!active) return;
                        if (
                            !("selection" in continuation) ||
                            continuation.return_path !== pendingCallback.path
                        )
                            throw new AdmissionRejected(
                                "The provider return does not match this page.",
                            );
                        if (continuation.selection) {
                            const selected = continuation.selection;
                            const directory = parseSessionTenancy(selected.session?.tenancy);
                            const project = directory?.projects.find(
                                (p) => p.project_id === selected.selected_project_id,
                            );
                            const company = directory?.companies.find(
                                (c) => c.company_id === selected.selected_company_id,
                            );
                            if (
                                !company ||
                                !project ||
                                project.company_id !== company.company_id ||
                                company.slug !== selected.canonical_company_slug ||
                                project.slug !== selected.canonical_project_slug
                            )
                                throw new AdmissionRejected(
                                    "The provider project could not be validated.",
                                );
                            // Reuse the verified continuation admission. The provider code stays in this tab's capture.
                            bootstrapAdmission.current = {
                                source: sourceIdentity,
                                value: selected,
                                deadline: credentialDeadline(selected, sentAt),
                                receivedAt,
                            };
                            callbackRestored.current = { state, path: pendingCallback.path };
                            replaceBootstrapTenantPath(
                                tenantPath(company.slug, project.slug, continuation.return_path),
                            );
                        } else {
                            callbackRestored.current = { state, path: pendingCallback.path };
                            setRestoringCallback(false);
                            setDiscoveryState("ready");
                            setAttempt((n) => n + 1);
                        }
                    })
                    .catch((failure) => {
                        if (active) {
                            if (
                                failure?.response?.status === 404 &&
                                isLegacyProviderReturnState(state)
                            ) {
                                callbackRestored.current = { state, path: pendingCallback.path };
                                setRestoringCallback(false);
                                setDiscoveryState("ready");
                                return;
                            }
                            setRestoringCallback(false);
                            setError({
                                message:
                                    "Could not restore the project for this provider connection. Retry to continue.",
                                authenticate: false,
                            });
                        }
                    });
                return () => {
                    active = false;
                };
            }
            // A verified legacy provider return finishes in its registered global
            // callback Document. Redirecting while its code exchange is pending
            // could redeem the same code twice on reload.
            if (callbackRestored.current?.path === legacyPathname) return;
            callbackRestored.current = null;
            // Bootstrap before mounting project data. Unmapped legacy pages retain
            // cookie/native/embed admission if optional discovery is unavailable.
            if (sourcePresent && !authFlow) {
                const discoveryKey = `${sourceIdentity}:${attempt}`;
                if (legacyDiscovery.current?.key !== discoveryKey) {
                    // Preserve pages only within one source admission. Login
                    // changes and explicit retries replace the directory.
                    setData(null);
                    setDiscoveryState("pending");
                    setRoutingChoices(null);
                    // A bare project link opens the project this session opened last, unless
                    // this tab just fell back from a switched-off project URL: that unprefixed
                    // path is the server's answer.
                    const hint =
                        hintSource &&
                        !pendingCallback &&
                        routeContract(window.location.pathname)?.scope === "project" &&
                        !fellBackFromProjectUrl()
                            ? readLastProject(hintSource)
                            : null;
                    legacyDiscovery.current = {
                        key: discoveryKey,
                        request: discoverLegacyEntry(hint),
                        hinted: hint !== null,
                    };
                }
                void legacyDiscovery.current.request
                    .then(async (entry) => {
                        if (!active) return;
                        if ("hinted" in entry) {
                            // Navigated off the project page meanwhile: discover it afresh.
                            if (routeContract(window.location.pathname)?.scope !== "project") {
                                legacyDiscovery.current = null;
                                setAttempt((n) => n + 1);
                                return;
                            }
                            // Canonicalize with the admission already held, as for `/session/me`.
                            bootstrapAdmission.current = {
                                source: sourceIdentity,
                                value: entry.hinted,
                                deadline: entry.deadline,
                                receivedAt: entry.receivedAt,
                            };
                            replaceBootstrapTenantPath(
                                tenantPath(
                                    entry.companySlug,
                                    entry.projectSlug,
                                    window.location.pathname +
                                        window.location.search +
                                        window.location.hash,
                                ),
                            );
                            return;
                        }
                        const { sentAt, receivedAt } = entry;
                        const response = { data: entry.data };
                        if (response.data.tenancy && !parseSessionTenancy(response.data.tenancy))
                            throw new Error("Invalid project directory");
                        const directory = parseSessionTenancy(response.data.tenancy);
                        const { project: bound, company } = sessionBoundProject(directory) ?? {};
                        const onProjectPage =
                            routeContract(window.location.pathname)?.scope === "project";
                        // Asked on a page with no project (Projects, sign-in, Not found) and then
                        // moved onto a project page in-app: the session's own project is not the
                        // one this person opened last. Ask again with the remembered project, as a
                        // bare project entry does, rather than sending them into the session's.
                        if (
                            onProjectPage &&
                            !legacyDiscovery.current?.hinted &&
                            hintSource &&
                            !pendingCallback &&
                            !fellBackFromProjectUrl() &&
                            readLastProject(hintSource)
                        ) {
                            legacyDiscovery.current = null;
                            setAttempt((n) => n + 1);
                            return;
                        }
                        if (bound && company && onProjectPage) {
                            // Admit once, then canonicalize without mounting the legacy
                            // project or doing a second network/bootstrap round trip.
                            try {
                                await ensureNativeTenantScopeSupport(
                                    !!response.data.routing_admission?.url_route_id,
                                );
                            } catch {
                                setData(response.data);
                                setDiscoveryState("ready");
                                return;
                            }
                            if (!active) return;
                            const admission = response.data.routing_admission;
                            bootstrapAdmission.current = admission
                                ? {
                                      source: sourceIdentity,
                                      value: admission,
                                      deadline: credentialDeadline(admission, sentAt),
                                      receivedAt,
                                  }
                                : null;
                            unrememberedRoute.current = entry.hintKept
                                ? `/${company.slug}/${bound.slug}`
                                : null;
                            replaceBootstrapTenantPath(
                                tenantPath(
                                    company.slug,
                                    bound.slug,
                                    window.location.pathname +
                                        window.location.search +
                                        window.location.hash,
                                ),
                            );
                            return;
                        }
                        // Re-running the local legacy redirect check must retain pages
                        // already appended to this admission's directory.
                        setData((current) => current ?? response.data);
                        setDiscoveryState("ready");
                    })
                    .catch(() => {
                        if (active) setDiscoveryState("failed");
                    });
            } else {
                setData(null);
                setRoutingChoices(null);
                legacyDiscovery.current = null;
            }
            return () => {
                active = false;
            };
        }
        if (!isTenantScopeAdmitted()) {
            // The page a switch left hands over the admission it prefetched for this one: taken
            // out of storage at bootstrap (`claimAdmissionHandoff`), taken here once, and preloaded
            // like discovery's only for this route and source session while fresh.
            const handed = takeAdmissionHandoff(tenantRoute(), hintSource);
            if (handed && !bootstrapAdmission.current)
                bootstrapAdmission.current = { source: sourceIdentity, ...handed };
        }
        if (!sourcePresent && !currentTabCompanySession()) {
            window.location.replace(
                `/authenticate?redirect=${encodeURIComponent(window.location.pathname + window.location.search + window.location.hash)}`,
            );
            return;
        }
        const admissionController = new AbortController();
        let pending: Promise<void> | null = null;
        // Whether this attempt still owns admission for the route it captured. Checked after any
        // await that precedes a global write (runtime key, tab credential, selected company) or
        // a navigation: the effect may have been cleaned up or replaced meanwhile.
        const ownsAdmission = (captured: ReturnType<typeof tenantRoute>) =>
            active &&
            !isTenantScopeDocumentClosed() &&
            tenantRoute().basename === captured.basename;
        const validate = (): Promise<void> => {
            if (pending) return pending;
            const captured = tenantRoute();
            const initialAdmission = !isTenantScopeAdmitted();
            // A renewal of the route this Document holds a credential for hands that credential
            // back, so the server refreshes its session instead of exchanging a new one.
            const tabSessionJwt =
                isTenantScopeAdmitted() && admittedRoute.current === captured.basename
                    ? currentTabCompanySession()?.sessionJwt
                    : undefined;
            // A selected-company JWT renewal keeps the scope this Document is
            // already admitted to. Do not retire that scope before we know it
            // changed: `getEmbedSessionJwt` enters this from inside the
            // interceptor of the request whose credential is expiring, and
            // that request has already captured the live generation and holds
            // the live scope signal. Retiring here bumps the generation and
            // aborts the signal, so the re-admission that made the request
            // valid is exactly what fails it -- including non-retried
            // mutations, and again on the reactive 401 path. Only a Document
            // with nothing admitted yet establishes scope here; that covers a
            // company-only admission (no project, so no `runtimeKey`) too, and
            // a genuine change of admitted identity is still caught below by
            // the `runtimeKey` comparison, which closes the Document.
            if (!isTenantScopeAdmitted() || admittedRoute.current !== captured.basename) {
                admittedRoute.current = null;
                resetTenantScope();
                setReady(false);
            }
            const quiet = quietRetry.current;
            quietRetry.current = false;
            if (!quiet) setError(null);
            admissionInFlight.current = admissionController;
            // The whole attempt ends by this deadline, every step that cannot be cancelled
            // included, so a request waiting on a renewal never waits longer.
            const budget = admissionDeadline(
                admissionController.signal,
                initialAdmission ? TIMEOUT_MS : RENEWAL_TIMEOUT_MS,
            );
            const operation = (async () => {
                try {
                    await budget.within(ensureNativeTenantScopeSupport());
                    if (!runtimeKey.current) await budget.within(clearAdmittedNativeScope());
                    if (!active) return;
                    // Bootstrap transport sends the source SDK/embed credential as it is, never
                    // this tab's expiring selected-company JWT: ai_engine authenticates the source
                    // session itself, so nothing here refreshes it first.
                    const candidate =
                        bootstrapAdmission.current?.source === sourceIdentity
                            ? bootstrapAdmission.current
                            : null;
                    // Discovery may have completed on a global page minutes ago.
                    const preloaded =
                        candidate && hasPreloadLife(candidate.deadline) ? candidate : null;
                    bootstrapAdmission.current = null;
                    const response = preloaded
                        ? {
                              data: preloaded.value,
                              deadline: preloaded.deadline,
                              receivedAt: preloaded.receivedAt,
                          }
                        : await budget.within(
                              requestCompanySession(
                                  {
                                      ...routeSelector(captured),
                                      ...(tabSessionJwt ? { tab_session_jwt: tabSessionJwt } : {}),
                                      // Only a first admission offers the answer's access body.
                                      ...(initialAdmission ? { include_project_access: true } : {}),
                                  },
                                  {
                                      timeout: budget.remainingMs(),
                                      retries: 2,
                                      backoffMs: initialAdmission
                                          ? admissionTiming.initialBackoffMs
                                          : admissionTiming.renewalBackoffMs,
                                      signal: budget.signal,
                                      assertCurrent: () => {
                                          if (!active || isTenantScopeDocumentClosed())
                                              throw new AdmissionRejected(
                                                  "Project sign-in was cancelled.",
                                              );
                                          if (tenantRoute().basename !== captured.basename)
                                              throw new AdmissionRejected(
                                                  "The project URL changed during sign-in.",
                                              );
                                      },
                                  },
                              ),
                          );
                    if (!active) return;
                    const { data: result, deadline, receivedAt } = response;
                    await budget.within(ensureNativeTenantScopeSupport(!!result.url_route_id));
                    const tenancy = parseSessionTenancy(result.session?.tenancy);
                    if (
                        !tenancy ||
                        !validIdentity(result.session) ||
                        typeof result.session_jwt !== "string" ||
                        !result.session_jwt ||
                        (result.access_project != null &&
                            (typeof result.access_project !== "string" ||
                                !result.access_project)) ||
                        (result.url_route_id && !result.access_project) ||
                        typeof result.legacy_url !== "boolean" ||
                        (result.canonical_url_alias !== undefined &&
                            typeof result.canonical_url_alias !== "boolean") ||
                        (result.acting_as_staff !== undefined &&
                            typeof result.acting_as_staff !== "boolean") ||
                        !Number.isSafeInteger(result.exp) ||
                        (result.expires_in !== undefined &&
                            (!Number.isSafeInteger(result.expires_in) || result.expires_in < 0)) ||
                        typeof result.selected_company_id !== "string" ||
                        (result.selected_project_id === null &&
                            result.canonical_project_slug !== null) ||
                        (result.selected_project_id !== null &&
                            typeof result.selected_project_id !== "string")
                    )
                        throw new AdmissionRejected(
                            "The server could not validate this project session.",
                        );
                    if (tenantRoute().basename !== captured.basename)
                        throw new AdmissionRejected("The project URL changed during sign-in.");
                    const company = tenancy.companies.find(
                        (c) => c.company_id === result.selected_company_id && c.state === "active",
                    );
                    const project =
                        tenancy.projects.find(
                            (p) =>
                                p.project_id === result.selected_project_id &&
                                p.company_id === company?.company_id &&
                                p.state === "active",
                        ) ?? null;
                    if (
                        !company ||
                        result.canonical_company_slug !== company.slug ||
                        (result.selected_project_id !== null &&
                            (!project || result.canonical_project_slug !== project.slug)) ||
                        (!result.legacy_url &&
                            !result.canonical_url_alias &&
                            captured.companySlug !== company.slug &&
                            captured.companySlug !== company.company_id) ||
                        (!result.legacy_url &&
                            !result.canonical_url_alias &&
                            captured.projectSlug &&
                            captured.projectSlug !== project?.slug &&
                            captured.projectSlug !== project?.project_id) ||
                        (tenancy.legacy_project_id &&
                            project &&
                            project.project_id !== tenancy.legacy_project_id)
                    )
                        throw new AdmissionRejected(
                            "This credential does not grant access to the project in this URL.",
                        );
                    // `is_platform_identity` says only that the person is staff; a server from
                    // before `acting_as_staff` admitted every staff member as staff.
                    const actingAsStaff = result.acting_as_staff ?? tenancy.is_platform_identity;
                    const credential = {
                        sessionJwt: result.session_jwt,
                        exp: result.exp,
                        organizationId: result.session.organization.organization_id,
                        lineageId: result.session.member.member_id,
                    };
                    const key = project
                        ? buildScopeKey({
                              principalId: result.session.member.member_id,
                              companyId: company.company_id,
                              projectId: project.project_id,
                              authorizationKey: buildCapabilityFingerprint(project.capabilities),
                          })
                        : null;
                    const nextRuntime = JSON.stringify([
                        key,
                        result.session.organization.organization_id,
                        tenancy.is_platform_identity,
                        actingAsStaff,
                        project?.role,
                        result.access_project ?? null,
                    ]);
                    if (runtimeKey.current && runtimeKey.current !== nextRuntime) {
                        await budget.within(clearAdmittedNativeScope({ revokeOffline: true }));
                        runtimeKey.current = null;
                        closeTenantScopeForDocument();
                        window.location.reload();
                        throw new AdmissionRejected("Project access changed. Reload this page.");
                    }
                    if (
                        project &&
                        (result.legacy_url ||
                            captured.companySlug !== company.slug ||
                            captured.projectSlug !== project.slug)
                    ) {
                        const suffix =
                            oauthTenantContinuation() ||
                            (result.legacy_url
                                ? window.location.pathname.slice(
                                      `/${captured.companySlug}`.length,
                                  ) || "/"
                                : captured.pathname);
                        replaceBootstrapTenantPath(
                            tenantPath(
                                company.slug,
                                project.slug,
                                suffix.startsWith("/oauth/authorize?")
                                    ? suffix
                                    : suffix + window.location.search + window.location.hash,
                            ),
                        );
                        return;
                    }
                    if (project && key) {
                        await budget.within(
                            installAdmittedNativeScope({
                                ...credential,
                                companyId: company.company_id,
                                companySlug: company.slug,
                                projectId: project.project_id,
                                projectSlug: project.slug,
                                principalId: result.session.member.member_id,
                                isPlatformIdentity: tenancy.is_platform_identity,
                                actingAsStaff,
                                capabilities: project.capabilities,
                                scopeKey: key,
                                ...(result.url_route_id ? { urlRouteId: result.url_route_id } : {}),
                                ...storageSlugField(projectStorageOrgSlug(project)),
                            }),
                        );
                        if (!ownsAdmission(captured)) {
                            await budget.within(clearAdmittedNativeScope());
                            throw new AdmissionRejected("The project URL changed during sign-in.");
                        }
                        const validated = {
                            scopeKey: key,
                            accessProject: result.access_project ?? undefined,
                            urlRouteId: result.url_route_id,
                            companyId: company.company_id,
                            projectId: project.project_id,
                            principalId: result.session.member.member_id,
                            organizationId: result.session.organization.organization_id,
                            storageOrgSlug: projectStorageOrgSlug(project),
                            actingAsStaff,
                        };
                        installValidatedScope(validated);
                        // A server that sends this scope's /project-access/me body with the
                        // admission saves the first /me request; an older one does not.
                        if (initialAdmission && result.project_access)
                            offerAdmittedAccess(validated, result.project_access, receivedAt);
                        // Only a legacy-bound project names the organization its legacy tabs
                        // cached its PDFs under; an adopted company project has none.
                        const legacyOrgSlug = projectStorageOrgSlug(project);
                        if (legacyOrgSlug)
                            supersedeLegacyFileCache(
                                result.session.member.member_id,
                                project.project_id,
                                legacyOrgSlug,
                            );
                        runtimeKey.current = nextRuntime;
                    }
                    installTabCompanySession({ ...credential, deadline });
                    installSelectedCompany(company.company_id);
                    admittedRoute.current = captured.basename;
                    setData({ ...result.session, tenancy });
                    setSelection({ company, project, actingAsStaff });
                    setError(null);
                    notices.admitted();
                    clearFallbackMarker();
                    clearRefusalReloadMarker();
                    clearCompanySignInMarker();
                    if (
                        initialAdmission &&
                        project &&
                        hintSource &&
                        unrememberedRoute.current !== captured.basename
                    )
                        rememberLastProject(hintSource, {
                            companySlug: company.slug,
                            projectSlug: project.slug,
                        });
                    unrememberedRoute.current = null;
                    setReady(true);
                } catch (failure) {
                    if (active) {
                        // A native app keeps its legacy session. A prefixed link to its own
                        // project drops the prefix; any other stays on the screen below.
                        const unprefixed = unprefixedUrl();
                        if (failure instanceof NativeTenantScopeUnsupportedError && unprefixed) {
                            const namesOwnProject = await budget
                                .within(namesSessionProject(captured))
                                .catch(() => false);
                            // Replaced or cleaned up while the session was read: no redirect, and
                            // the attempt that owns admission shows its own outcome.
                            if (!ownsAdmission(captured)) throw failure;
                            if (namesOwnProject) {
                                window.location.replace(unprefixed);
                                throw failure;
                            }
                        }
                        const { status, code, legacyPath, allowedAuthMethods } =
                            refusalInfo(failure);
                        const disabled = status === 403 && code === REFUSAL.disabled;
                        if (disabled && redirectToUnprefixedRoute(failure)) {
                            // The legacy path admits through the browser session.
                            closeTenantScopeForDocument();
                            throw failure;
                        }
                        const authenticationRequired =
                            status === 409 && code === REFUSAL.authRequired;
                        const signInMethods = authenticationRequired ? allowedAuthMethods : null;
                        const refused = [401, 403, 409].includes(status ?? 0);
                        const ownMessage =
                            failure instanceof AdmissionRejected ||
                            failure instanceof NativeTenantScopeUnsupportedError;
                        // A renewal that fails while this tab's credential is still good changes
                        // nothing on screen: requests keep carrying it, and the next renewal is
                        // due once the floor passes. Only a credential past its deadline lets
                        // the outage show.
                        if (
                            !initialAdmission &&
                            !refused &&
                            status !== 404 &&
                            !ownMessage &&
                            usableTabCompanySession()
                        ) {
                            deferRenewal();
                            throw failure;
                        }
                        const closedByRenewal =
                            Boolean(runtimeKey.current) &&
                            (failure instanceof AdmissionRejected || refused);
                        if (closedByRenewal) {
                            runtimeKey.current = null;
                            closeTenantScopeForDocument();
                        }
                        await budget
                            .within(
                                clearAdmittedNativeScope(
                                    failure instanceof AdmissionRejected || refused
                                        ? { revokeOffline: true }
                                        : undefined,
                                ),
                            )
                            .catch(() => undefined);
                        const lost = !initialAdmission || disabled;
                        // A step-up that names its method asks for it again after a same-tab
                        // sign-in; only one that names none can loop into "no access".
                        const loopedSignIn =
                            authenticationRequired && !signInMethods && companySignInWasTried();
                        let refusal: RefusalScreen | undefined;
                        if (status === 404) refusal = "not_found";
                        // The unprefixed path would open the browser session's other organization.
                        else if (disabled && legacyPath === false) refusal = "disabled";
                        else if (
                            refused &&
                            status !== 401 &&
                            code !== REFUSAL.inactive &&
                            (!authenticationRequired || loopedSignIn)
                        )
                            refusal = lost ? "lost_access" : "no_access";
                        const transient =
                            !refusal &&
                            !refused &&
                            !ownMessage &&
                            isTransientAdmissionFailure(failure);
                        const message =
                            status === 401
                                ? "Your sign-in has expired. Sign in again to return to this project."
                                : authenticationRequired && !loopedSignIn
                                  ? signInMethods
                                      ? `${signInRequirement(signInMethods)} Your existing session is intact.`
                                      : "Your existing session is intact. Sign in to this company to complete its authentication requirements."
                                  : ownMessage
                                    ? failure instanceof NativeTenantScopeUnsupportedError
                                        ? "This app opens the project you are signed in to. Open this link in your browser, or continue with your project."
                                        : (failure as Error).message
                                    : "Project access is temporarily unavailable. Your existing session is intact.";
                        setError({
                            message,
                            authenticate:
                                (authenticationRequired && !loopedSignIn) || status === 401,
                            sessionExpired: status === 401,
                            unsupported: failure instanceof NativeTenantScopeUnsupportedError,
                            refusal,
                            signInMethods,
                            transient,
                        });
                        setReady(false);
                        const notice = {
                            kind: initialAdmission ? ("first_load" as const) : ("renewal" as const),
                            status,
                            code: ownMessage
                                ? failure instanceof NativeTenantScopeUnsupportedError
                                    ? "native_unsupported"
                                    : "admission_rejected"
                                : admissionFailureCode(failure),
                            tokenValid: !!usableTabCompanySession(),
                            refusal: refusal ?? null,
                        };
                        notices.show(JSON.stringify([refusal ?? null, message]), notice);
                        // This Document is closed, so the chooser cannot load in it: a fresh
                        // Document re-renders the refusal through initial admission. That one
                        // meets the same answer, which this Document has just reported.
                        if (closedByRenewal && refused && reloadForRefusal())
                            notices.reportedBeforeReload(notice);
                    }
                    throw failure;
                } finally {
                    budget.release();
                    pending = null;
                    if (admissionInFlight.current === admissionController)
                        admissionInFlight.current = null;
                }
            })();
            pending = operation;
            return operation;
        };
        setTenantSessionRevalidator(validate);
        void validate().catch(() => undefined);
        const pagehide = () => {
            admissionController.abort();
            closeTenantScopeForDocument();
        };
        const pageshow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                closeTenantScopeForDocument();
                window.location.reload();
            }
        };
        window.addEventListener("pagehide", pagehide);
        window.addEventListener("pageshow", pageshow);
        return () => {
            active = false;
            admissionController.abort();
            setTenantSessionRevalidator(null);
            window.removeEventListener("pagehide", pagehide);
            window.removeEventListener("pageshow", pageshow);
        };
    }, [
        sourceReady,
        sourcePresent,
        canonical,
        basename,
        legacyPathname,
        attempt,
        authFlow,
        hintSource,
        sourceIdentity,
        notices,
        setData,
        setDiscoveryState,
        setError,
        setReady,
        setRoutingChoices,
        setSelection,
    ]);

    // A laptop waking from sleep or a network coming back recovers without a click. The poll
    // doubles from 20 s to 5 minutes; coming online or into view tries at once and starts it over.
    const transient = !!error?.transient;
    useEffect(() => {
        if (!canonical || !transient) return;
        const retry = () => {
            // An admission already running owns its deadline and backoff; a second trigger would
            // abort it and reset both, and fail a request waiting on its renewal.
            if (admissionInFlight.current) return;
            quietRetry.current = true;
            setAttempt((n) => n + 1);
        };
        let delay = admissionTiming.pollMs;
        let timer: number | undefined;
        const poll = () => {
            timer = window.setTimeout(() => {
                retry();
                delay = Math.min(delay * 2, admissionTiming.maxPollMs);
                poll();
            }, delay);
        };
        const retryNow = () => {
            window.clearTimeout(timer);
            delay = admissionTiming.pollMs;
            retry();
            poll();
        };
        const onVisible = () => {
            if (document.visibilityState === "visible") retryNow();
        };
        window.addEventListener("online", retryNow);
        document.addEventListener("visibilitychange", onVisible);
        poll();
        return () => {
            window.removeEventListener("online", retryNow);
            document.removeEventListener("visibilitychange", onVisible);
            window.clearTimeout(timer);
        };
    }, [canonical, transient]);
    useEffect(() => {
        if (!canonical) return;
        if (
            sourcePrincipal.current &&
            sourcePrincipal.current !== sourceIdentity &&
            runtimeKey.current
        ) {
            closeTenantScopeForDocument();
            installTabCompanySession(null);
            window.location.reload();
        }
        sourcePrincipal.current = sourceIdentity;
    }, [canonical, sourceIdentity]);
    useEffect(() => {
        const open = () => setSwitcherOpen(true);
        window.addEventListener("primepoint:open-project-switcher", open);
        return () => window.removeEventListener("primepoint:open-project-switcher", open);
    }, []);
    const tenancy = data?.tenancy;
    // The staff registry holds no project, on the server's word: project pages ask for one,
    // and a project-free page (the Admin console) runs with a project-free query client.
    const staffRegistrySession =
        !canonical && (data as StaffRegistryAware | null)?.staff_registry_session === true;
    const staffRegistryOrganizationId =
        (data as StaffRegistryAware | null)?.staff_registry_organization_id ?? null;
    // A host that cannot run on project URLs keeps switching by exchanging its session (the
    // legacy switcher). A refusal screen has no admitted directory yet; the chooser reads the
    // person's own list.
    const routingAvailable =
        (getPlatform().projectAdmission.supportsUrlRouting &&
            (data?.project_urls_available || tenancy?.url_routing)) ||
        !!error?.refusal;
    const choices = routingChoices ?? tenancy;
    const loadMoreProjects = React.useCallback(async () => {
        if (projectsLoading) return;
        if (routingAvailable) {
            const cursor = routingChoices?.projects_next_cursor;
            if (routingChoices && !cursor) return;
            const captured = captureTenantScopeBoundary();
            setProjectsLoading(true);
            try {
                const response = await axios.get<SessionTenancy>("/api/ai/session/project-routes", {
                    params: cursor ? { after_project_id: cursor } : {},
                    timeout: TIMEOUT_MS,
                });
                captured.assertCurrent();
                const page = parseSessionTenancy(response.data);
                if (!page?.url_routing) throw new AdmissionRejected("Invalid project routing page");
                setRoutingChoices((previous) =>
                    previous
                        ? {
                              ...page,
                              projects: [
                                  ...previous.projects,
                                  ...page.projects.filter(
                                      (p) =>
                                          !previous.projects.some(
                                              (o) => o.project_id === p.project_id,
                                          ),
                                  ),
                              ],
                              companies: [
                                  ...previous.companies,
                                  ...page.companies.filter(
                                      (c) =>
                                          !previous.companies.some(
                                              (o) => o.company_id === c.company_id,
                                          ),
                                  ),
                              ],
                          }
                        : page,
                );
            } finally {
                setProjectsLoading(false);
            }
            return;
        }
        const cursor = tenancy?.projects_next_cursor;
        if (!cursor) return;
        const captured = captureTenantScopeBoundary();
        setProjectsLoading(true);
        try {
            const response = await axios.get<{
                projects: TenantProject[];
                next_cursor: string | null;
            }>("/api/ai/session/projects", {
                params: { after_project_id: cursor },
                timeout: TIMEOUT_MS,
            });
            captured.assertCurrent();
            const merged = parseSessionTenancy({
                ...tenancy,
                projects: [
                    ...tenancy.projects,
                    ...response.data.projects.filter(
                        (p) => !tenancy.projects.some((old) => old.project_id === p.project_id),
                    ),
                ],
                projects_next_cursor: response.data.next_cursor,
            });
            if (!merged) throw new AdmissionRejected("Invalid project directory page");
            setData((previous) => (previous ? { ...previous, tenancy: merged } : previous));
        } finally {
            setProjectsLoading(false);
        }
    }, [
        tenancy,
        projectsLoading,
        routingAvailable,
        routingChoices,
        setData,
        setProjectsLoading,
        setRoutingChoices,
    ]);
    const [companiesLoading, setCompaniesLoading] = useSourceState(sourceAdmission, false);
    const loadMoreCompanies = React.useCallback(async () => {
        const cursor = tenancy?.companies_next_cursor;
        if (!cursor || companiesLoading) return;
        setCompaniesLoading(true);
        try {
            const response = await axios.get<{
                companies: TenantCompany[];
                next_cursor: string | null;
            }>("/api/ai/session/companies", {
                params: { after_company_id: cursor },
                timeout: TIMEOUT_MS,
            });
            const merged = parseSessionTenancy({
                ...tenancy,
                companies: [
                    ...tenancy.companies,
                    ...response.data.companies.filter(
                        (company) =>
                            !tenancy.companies.some((old) => old.company_id === company.company_id),
                    ),
                ],
                companies_next_cursor: response.data.next_cursor,
            });
            if (!merged) throw new AdmissionRejected("Invalid company directory page");
            setData((previous) => (previous ? { ...previous, tenancy: merged } : previous));
        } finally {
            setCompaniesLoading(false);
        }
    }, [tenancy, companiesLoading, setCompaniesLoading, setData]);
    const [switching, setSwitching] = useState(false);
    // The switch waiting for its prefetch, if any: only the latest may leave, and only from where
    // it was chosen. Cleared on unmount, so a provider that is gone never navigates.
    const pendingSwitch = useRef<{ key: string } | null>(null);
    useEffect(
        () => () => {
            pendingSwitch.current = null;
        },
        [],
    );
    // The page the person is on now: a switch leaves only from the page it was chosen on.
    const currentPage = useRef(location.key);
    currentPage.current = location.key;
    const value = useMemo<TenantScopeValue>(() => {
        /** A listed project and its company, by project id. */
        const choiceOf = (id: string) => {
            const project = choices?.projects.find((p) => p.project_id === id);
            const company = choices?.companies.find((c) => c.company_id === project?.company_id);
            return project && company ? { project, company } : null;
        };
        const suffixFor = (company: TenantCompany, project?: TenantProject) => {
            const current = tenantRoute();
            return (
                oauthTenantContinuation() ||
                projectPickerContinuation() ||
                // A page outside every project (Select a project, the portfolio) opens the
                // chosen project's Home: carried along, /projects would reopen the chooser.
                (routeContract(current.pathname)?.scope !== "project"
                    ? "/"
                    : !current.companySlug ||
                        (current.companySlug === company.slug &&
                            current.projectSlug === project?.slug)
                      ? current.pathname + window.location.search + window.location.hash
                      : projectSwitchPath(current.pathname))
            );
        };
        const navigate = (company: TenantCompany, project?: TenantProject) => {
            const continuation = oauthTenantContinuation();
            const suffix = suffixFor(company, project);
            const family = projectSwitchPath(suffix);
            const pickerSearch = continuation
                ? `?pp_oauth_request=${encodeURIComponent(continuation)}`
                : family === "/"
                  ? ""
                  : `?pp_project_route=${encodeURIComponent(family)}`;
            const target: AdmissionRoute | null =
                project && hintSource
                    ? { companySlug: company.slug, projectSlug: project.slug }
                    : null;
            const leave = () => {
                if (target && hintSource) handOffPrefetchedAdmission(target, hintSource);
                // pagehide closes the scope, and writes the staged handoff, once navigation
                // actually happens. A native beforeunload cancellation must leave this Document
                // usable.
                window.location.assign(
                    project
                        ? tenantPath(company.slug, project.slug, suffix)
                        : `/${company.slug}/${pickerSearch}`,
                );
            };
            // A prefetch on its way is worth waiting for, up to its own timeout: the next page then
            // skips company-session, whose own request would start only after the page load. One
            // that fails or runs out leaves without it, and the next page admits as it would have.
            // The wait comes before the leave is asked, so a confirmed discard still navigates
            // inside its own callback, where the unsaved-changes prompt knows it has consent.
            const key = `${company.slug}/${project?.slug ?? ""}`;
            // A second click on the project already waiting changes nothing.
            if (pendingSwitch.current?.key === key) return;
            const landing = target && hintSource ? prefetchLanding(target, hintSource) : null;
            if (!landing) {
                pendingSwitch.current = null;
                setSwitching(false);
                requestLeave(leave);
                return;
            }
            // The wait can last as long as the prefetch has left: show it on the switcher. It ends
            // before the leave is asked, so an unsaved-changes prompt never shows a spinner too.
            const chosen = { key };
            const chosenAt = currentPage.current;
            pendingSwitch.current = chosen;
            setSwitching(true);
            void landing.then(() => {
                // Superseded by a later choice, or the provider is gone.
                if (pendingSwitch.current !== chosen) return;
                pendingSwitch.current = null;
                setSwitching(false);
                // The person moved on while it waited: another page, or another leave already
                // asking about unsaved changes, whose Discard must keep its own destination.
                if (currentPage.current !== chosenAt || isLeaveQuestionPending()) return;
                requestLeave(leave);
            });
        };
        const project = selection?.project;
        const linked = canonical
            ? selection?.company && project
                ? { company: selection.company, project }
                : null
            : sessionBoundProject(tenancy);
        return {
            projectUrlsAvailable: !!routingAvailable,
            linkProject: linked
                ? { companySlug: linked.company.slug, projectSlug: linked.project.slug }
                : null,
            loadProjectChoices: routingAvailable && !routingChoices ? loadMoreProjects : undefined,
            projectHref: (id) => {
                const choice = choiceOf(id);
                return choice
                    ? tenantPath(
                          choice.company.slug,
                          choice.project.slug,
                          suffixFor(choice.company, choice.project),
                      )
                    : null;
            },
            // The browser's own session on project URLs only: an embed panel or a native host
            // switches by moving its session (`hintSource` is null there).
            prefetchProject:
                hintSource && routingAvailable
                    ? (id) => {
                          const choice = choiceOf(id);
                          if (choice && id !== selection?.project?.project_id)
                              prefetchAdmission(
                                  {
                                      companySlug: choice.company.slug,
                                      projectSlug: choice.project.slug,
                                  },
                                  hintSource,
                              );
                      }
                    : undefined,
            switching,
            hasMoreProjects: !!choices?.projects_next_cursor,
            hasMoreCompanies: !routingAvailable && !!tenancy?.companies_next_cursor,
            projectsLoading,
            companiesLoading,
            loadMoreProjects,
            loadMoreCompanies,
            mode: canonical ? "canonical" : "legacy",
            authScope: tenancy?.auth_scope,
            discoveryState,
            retryDiscovery: () => setAttempt((n) => n + 1),
            companies: choices?.companies ?? EMPTY.companies,
            projects: choices?.projects ?? EMPTY.projects,
            selectedCompany: selection?.company ?? null,
            selectedProject: project ?? null,
            capabilities: project ? new Set(project.capabilities) : EMPTY_CAPABILITIES,
            scopeKey:
                project && data
                    ? buildScopeKey({
                          principalId: data.member.member_id,
                          companyId: project.company_id,
                          projectId: project.project_id,
                          authorizationKey: buildCapabilityFingerprint(project.capabilities),
                      })
                    : null,
            sessionMember: canonical ? data?.member : undefined,
            sessionOrganization: canonical ? data?.organization : undefined,
            session: canonical ? data?.session : undefined,
            bootstrapIdentity:
                !canonical &&
                discoveryState === "ready" &&
                legacyDiscovery.current?.key === `${sourceIdentity}:${attempt}` &&
                data
                    ? { member: data.member, organization: data.organization }
                    : undefined,
            isPlatformIdentity: tenancy?.is_platform_identity ?? false,
            actingAsStaff: selection?.actingAsStaff ?? false,
            staffRegistrySession,
            staffRegistryOrganizationId,
            isMasterOrganization: tenancy?.master_organization === true,
            loading: canonical && !ready && !error,
            scopeValidated: canonical && ready && !!project && !error,
            selectCompany: (id) => {
                const company = tenancy?.companies.find((c) => c.company_id === id);
                if (company) navigate(company);
            },
            selectProject: (id) => {
                const choice = choiceOf(id);
                if (choice) navigate(choice.company, choice.project);
            },
        };
    }, [
        canonical,
        tenancy,
        data,
        selection,
        ready,
        error,
        projectsLoading,
        companiesLoading,
        loadMoreProjects,
        loadMoreCompanies,
        routingAvailable,
        routingChoices,
        choices,
        discoveryState,
        sourceIdentity,
        attempt,
        staffRegistrySession,
        staffRegistryOrganizationId,
        hintSource,
        switching,
    ]);
    return (
        <TenantScopeContext.Provider value={value}>
            <Modal open={switcherOpen} onClose={() => setSwitcherOpen(false)}>
                <ModalDialog>
                    <ModalClose />
                    <Typography level="h3">Select a project</Typography>
                    <TenantScopeSwitcher color="neutral" />
                </ModalDialog>
            </Modal>
            {!sourceReady && !authFlow && routeContract(route.pathname)?.scope === "project" ? (
                <AdmissionShell />
            ) : (restoringCallback ||
                  (sourcePresent &&
                      !authFlow &&
                      discoveryState === "pending" &&
                      // A project-free page waits for the staff registry answer too: it
                      // chooses whether the page asks the server to admit a project.
                      (routeContract(route.pathname)?.scope === "project" ||
                          routeContract(route.pathname)?.projectFree))) &&
              !canonical ? (
                <AdmissionShell />
            ) : !canonical &&
              !error &&
              (staffRegistrySession ||
                  (tenancy?.auth_scope === "company" &&
                      !tenancy.is_platform_identity &&
                      !tenancy.legacy_project_id &&
                      !tenancy.canonical_redirect_project_id)) &&
              routeContract(route.pathname)?.scope === "project" ? (
                <AdmissionShell>
                    <Stack sx={styles.status} spacing={2}>
                        <Typography level="h2">Select a project to open this link</Typography>
                        <TenantScopeSwitcher color="neutral" />
                    </Stack>
                </AdmissionShell>
            ) : !canonical && !error ? (
                children
            ) : error ? (
                <AdmissionShell>
                    <Stack sx={styles.status} spacing={2}>
                        {error.refusal ? (
                            <AdmissionRefusal screen={error.refusal} />
                        ) : (
                            <Alert color="warning">{error.message}</Alert>
                        )}
                        {error.refusal ? null : error.unsupported ? (
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="plain"
                                    onClick={() =>
                                        openInBrowser(
                                            new URL(
                                                window.location.pathname +
                                                    window.location.search +
                                                    window.location.hash,
                                                getPlatform().bootstrap.apiOrigin,
                                            ).toString(),
                                        )
                                    }
                                >
                                    Open in browser
                                </Button>
                                <Button onClick={() => window.location.assign("/")}>
                                    Continue with existing project
                                </Button>
                            </Stack>
                        ) : error.authenticate ? (
                            <CompanySignInButton
                                sessionExpired={!!error.sessionExpired}
                                methods={error.signInMethods ?? null}
                                email={sourceMember.member?.email_address}
                            />
                        ) : (
                            <Button
                                onClick={() =>
                                    isTenantScopeDocumentClosed()
                                        ? window.location.reload()
                                        : setAttempt((n) => n + 1)
                                }
                            >
                                Retry
                            </Button>
                        )}
                    </Stack>
                </AdmissionShell>
            ) : !ready ? (
                <AdmissionShell />
            ) : !selection?.project ? (
                <AdmissionShell>
                    <Stack sx={styles.status} spacing={2}>
                        <Typography level="h2">Select a project</Typography>
                        <TenantScopeSwitcher color="neutral" />
                    </Stack>
                </AdmissionShell>
            ) : null}
            {canonical && (
                <Box display={ready && !error ? "contents" : "none"}>
                    {runtimeKey.current ? children : null}
                </Box>
            )}
        </TenantScopeContext.Provider>
    );
}
